import { withRouter } from 'react-router-dom';
import { Sticky } from 'react-sticky';

import Field from 'Component/Field';
import FIELD_TYPE from 'Component/Field/Field.config';
import PRODUCT_TYPE from 'Component/Product/Product.config';
import ProductAdditionalInfo from 'Component/ProductAdditionalInfo';
import ProductReviewRating from 'Component/ProductReviewRating';
import TextPlaceholder from 'Component/TextPlaceholder';
import TypographyHeader from 'Component/TypographyHeader';
import { ProductActions as SourceProductActions } from 'SourceComponent/ProductActions/ProductActions.component';
import { isCrawler, isSSR } from 'Util/Browser';
import { VALIDATION_INPUT_TYPE_NUMBER } from 'Util/Validator/Config';

import './ProductActions.style';

/** @namespace Advox/Component/ProductActions/Component */
export class ProductActions extends SourceProductActions {
    componentDidUpdate(prevProps) {
        super.componentDidUpdate(prevProps);

        const { quantity: prevQuantity } = prevProps;
        const { quantity, reloadProduct } = this.props;

        if (quantity !== prevQuantity) {
            reloadProduct(quantity);
        }
    }

    renderQuantityChanger() {
        const {
            quantity,
            minQuantity,
            maxQuantity,
            setQuantity,
            inStock,
            product: { type_id },
        } = this.props;

        if (type_id === PRODUCT_TYPE.grouped) {
            return null;
        }

        return (
            <Field
                type={FIELD_TYPE.number}
                attr={{
                    id: 'item_qty',
                    name: 'item_qty',
                    defaultValue: quantity,
                    max: maxQuantity,
                    min: minQuantity,
                }}
                validationRule={{
                    inputType: VALIDATION_INPUT_TYPE_NUMBER.numeric,
                    isRequired: true,
                    range: {
                        min: minQuantity,
                        max: maxQuantity,
                    },
                }}
                isDisabled={!inStock}
                mix={{ block: this.className, elem: 'Qty' }}
                events={{ onChange: setQuantity }}
                validateOn={['onChange']}
            />
        );
    }

    renderRatingSummary() {
        const {
            product: { review_summary: { rating_summary, review_count } = {} },
        } = this.props;

        return <ProductReviewRating summary={rating_summary || 0} count={review_count} size="normal" />;
    }

    renderReviewSection() {
        return (
            <div block="ProductActions" elem="Reviews">
                {this.renderRatingSummary()}
            </div>
        );
    }

    renderName() {
        const {
            product: { name },
        } = this.props;
        const nameToRender = name;

        return (
            <TypographyHeader variant="normal" mix={{ block: 'ProductActions', elem: 'Title' }} itemProp="name">
                <TextPlaceholder content={nameToRender} length="medium" />
            </TypographyHeader>
        );
    }

    renderAdditionalInfoBlock() {
        const {
            product: { calculated_delivery_time, calculated_delivery_cost },
            showAlternativeDesign,
        } = this.props;

        return (
            <ProductAdditionalInfo
                calculatedDeliveryTime={calculated_delivery_time}
                calculatedDeliveryCost={calculated_delivery_cost}
                showAlternativeDesign={showAlternativeDesign}
            />
        );
    }

    renderAddToCartMobile() {
        const { showFixedAddToCart } = this.props;

        return (
            <div
                block="ProductActions"
                elem="AddToCartFixed"
                mods={{ isPrerendered: isSSR() || isCrawler(), visible: showFixedAddToCart }}
            >
                {this.renderAddToCartButton()}
                {this.renderWishlistButton()}
            </div>
        );
    }

    renderAddToCartActionBlock() {
        return (
            <div
                id="ProductActionsAddToCartWrapper"
                block="ProductActions"
                elem="AddToCartWrapper"
                mods={{ isPrerendered: isSSR() || isCrawler() }}
            >
                {this.renderQuantityChanger()}
                {this.renderAddToCartButton()}
                <div block="ProductActions" elem="ActionButtons">
                    {this.renderWishlistButton()}
                    {this.renderCompareButton()}
                </div>
            </div>
        );
    }

    renderDesktop() {
        return (
            <>
                {this.renderName()}
                {this.renderReviewSection()}
                <div block="ProductActions" elem="Wrapper">
                    {this.renderPriceWithGlobalSchema()}
                    {this.renderConfigurableOptions()}
                    {this.renderCustomAndBundleOptions()}
                    {this.renderGroupedOptions()}
                    {this.renderDownloadableSamples()}
                    {this.renderDownloadableLinks()}
                    {this.renderTierPrices()}
                    {this.renderAddToCartActionBlock()}
                    {this.renderAdditionalInfoBlock()}
                </div>
            </>
        );
    }

    renderMobile() {
        return (
            <>
                {this.renderTierPrices()}
                {this.renderPriceWithGlobalSchema()}
                <div block="ProductActions" elem="Wrapper">
                    {this.renderReviewSection()}
                    {this.renderConfigurableOptions()}
                    {this.renderCustomAndBundleOptions()}
                    {this.renderGroupedOptions()}
                    {this.renderDownloadableSamples()}
                    {this.renderDownloadableLinks()}
                    {this.renderAddToCartActionBlock()}
                    {this.renderAdditionalInfoBlock()}
                    {this.renderAddToCartMobile()}
                </div>
            </>
        );
    }

    wrapWithSticky(content) {
        const { showAlternativeDesign, calculateStyles } = this.props;

        if (showAlternativeDesign) {
            return (
                <Sticky>
                    {({ style, distanceFromTop, isSticky }) => (
                        <div
                            block="ProductActions"
                            elem="StickyContent"
                            style={{ ...style, ...calculateStyles(style, distanceFromTop, isSticky) }}
                        >
                            {content}
                        </div>
                    )}
                </Sticky>
            );
        }

        return content;
    }

    render() {
        const { device: { isMobile, isTablet } = {}, setValidator } = this.props;

        return (
            <div id="ProductActions-Main">
                {this.wrapWithSticky(
                    <article block="ProductActions" ref={(elem) => setValidator(elem)}>
                        {isMobile || isTablet ? this.renderMobile() : this.renderDesktop()}
                    </article>
                )}
            </div>
        );
    }
}

export default withRouter(ProductActions);
